import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { getCurrentLocale } from '@/i18n'

/**
 * 导出配置选项
 */
export interface ExportOptions {
  /** 文件名（不含扩展名） */
  filename?: string
  /** 表格标题 */
  title?: string
  /** 是否包含表头 */
  includeHeader?: boolean
  /** 数据格式化函数 */
  formatData?: (value: any, dataIndex: string, record: any) => any
}

/**
 * 判断列是否可以导出
 * @param column 列配置
 * @returns 是否可以导出
 */
const isExportableColumn = (column: any): boolean => {
  console.log('🔍 [EXPORT] 检查列是否可导出:', {
    dataIndex: column.dataIndex,
    title: column.title,
    type: column.type,
    slotName: column.slotName
  })

  // 必须有dataIndex
  if (!column.dataIndex) {
    console.log('❌ [EXPORT] 跳过列: 没有dataIndex')
    return false
  }

  // 排除常见的操作列dataIndex
  const actionDataIndexes = ['actions', 'action', 'operation', 'opt', 'handle', 'operate', 'options']
  if (actionDataIndexes.includes(column.dataIndex.toLowerCase())) {
    console.log('❌ [EXPORT] 跳过列: 操作列dataIndex -', column.dataIndex)
    return false
  }

  // 排除复选框列
  if (column.type === 'selection' || column.dataIndex === 'selection') {
    console.log('❌ [EXPORT] 跳过列: 复选框列')
    return false
  }

  // 排除序号列
  const indexDataIndexes = ['index', 'number', 'no', 'seq', 'rowindex', 'serialnumber']
  if (indexDataIndexes.includes(column.dataIndex.toLowerCase())) {
    console.log('❌ [EXPORT] 跳过列: 序号列 -', column.dataIndex)
    return false
  }

  // 排除操作相关的插槽
  if (column.slotName && (
    column.slotName.toLowerCase().includes('action') ||
    column.slotName.toLowerCase().includes('operation') ||
    column.slotName.toLowerCase().includes('opt') ||
    column.slotName.toLowerCase().includes('handle')
  )) {
    console.log('❌ [EXPORT] 跳过列: 操作插槽 -', column.slotName)
    return false
  }

  // 通过标题判断（支持中英文）
  if (column.title && typeof column.title === 'string') {
    const title = column.title.toLowerCase()
    const excludeKeywords = ['操作', 'action', '序号', 'index', '选择', 'select', 'checkbox']
    if (excludeKeywords.some(keyword => title.includes(keyword))) {
      console.log('❌ [EXPORT] 跳过列: 标题匹配排除关键词 -', column.title)
      return false
    }
  }

  console.log('✅ [EXPORT] 列可导出:', column.dataIndex)
  return true
}

/**
 * 导出Excel文件（使用ExcelJS）
 * @param data 表格数据
 * @param columns 列配置
 * @param options 导出选项
 */
export const exportToExcel = async(
  data: any[],
  columns: any[],
  options: ExportOptions = {}
) => {
  try {
    console.log('📊 [EXPORT] 开始导出Excel...', { data: data.length, columns: columns.length })

    const {
      filename = `表格数据_${new Date().toISOString().split('T')[0]}`,
      title = '表格数据',
      includeHeader = true,
      formatData
    } = options

    // 过滤可导出的列
    const exportColumns = columns.filter(isExportableColumn)
    console.log('📋 [EXPORT] 过滤后的导出列:', exportColumns.map(col => col.dataIndex))

    // 创建工作簿和工作表
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('数据表')

    let currentRow = 1

    // 添加标题
    if (title && includeHeader) {
      const titleRow = worksheet.getRow(currentRow)
      titleRow.getCell(1).value = title

      // 合并标题单元格
      if (exportColumns.length > 1) {
        worksheet.mergeCells(currentRow, 1, currentRow, exportColumns.length)
      }

      // 设置标题样式
      titleRow.getCell(1).style = {
        font: { bold: true, size: 14 },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } },
        border: {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        }
      }
      titleRow.height = 25

      currentRow++ // 标题后直接接表头，不空行
    }

    // 添加表头
    if (includeHeader) {
      const headerRow = worksheet.getRow(currentRow)
      exportColumns.forEach((col, index) => {
        const cell = headerRow.getCell(index + 1)
        cell.value = col.title || col.dataIndex

        // 设置表头样式
        cell.style = {
          font: { bold: true, size: 11 },
          alignment: { horizontal: 'center', vertical: 'middle' },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE9ECEF' } },
          border: {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          }
        }
      })
      headerRow.height = 20
      currentRow++
    }

    // 添加数据行
    data.forEach(row => {
      const dataRow = worksheet.getRow(currentRow)

      exportColumns.forEach((col, index) => {
        const cell = dataRow.getCell(index + 1)
        let value = row[col.dataIndex]

        // 使用自定义格式化函数（如果提供）
        if (formatData) {
          value = formatData(value, col.dataIndex, row)
        }

        cell.value = value || ''

        // 设置数据行样式
        cell.style = {
          font: { size: 10 },
          alignment: { horizontal: 'left', vertical: 'middle' },
          border: {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          }
        }
      })

      dataRow.height = 18
      currentRow++
    })

    // 设置列宽（只设置有数据的列）
    exportColumns.forEach((col, index) => {
      const column = worksheet.getColumn(index + 1)
      const headerLength = (col.title || '').length
      const maxDataLength = Math.max(
        ...data.map(row => {
          const value = formatData ? formatData(row[col.dataIndex], col.dataIndex, row) : row[col.dataIndex]
          return String(value || '').length
        }),
        0
      )
      const optimalWidth = Math.max(headerLength, maxDataLength, 8) + 2
      column.width = Math.min(optimalWidth, 50)
    })

    // 设置工作表的使用范围，避免多余的空白列
    if (exportColumns.length > 0 && data.length > 0) {
      const endColumn = String.fromCharCode(64 + exportColumns.length) // A=65, so 64+1=A
      const endRow = currentRow - 1
      worksheet.pageSetup = {
        printArea: `A1:${endColumn}${endRow}`,
        fitToPage: true,
        fitToWidth: 1
      }
    }

    // 冻结窗格（冻结表头）
    if (includeHeader) {
      const freezeRow = title ? 3 : 2
      worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: freezeRow - 1 }]
    }

    // 导出文件
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })

    let filenameNoExt = filename || ''
    if (filenameNoExt.endsWith('.xlsx')) {
      filenameNoExt = filenameNoExt.substring(0, filenameNoExt.length - 5)
    }
    saveAs(blob, `${filenameNoExt}.xlsx`)

    console.log('✅ [EXPORT] Excel导出成功:', `${filenameNoExt}.xlsx`)
    return true

  } catch (error: any) {
    console.error('❌ [EXPORT] Excel导出失败:', error)
    throw new Error('Excel导出失败')
  }
}

/**
 * 导出CSV文件
 * @param data 表格数据
 * @param columns 列配置
 * @param options 导出选项
 */
export const exportToCSV = (
  data: any[],
  columns: any[],
  options: ExportOptions = {}
) => {
  try {
    console.log('📊 [EXPORT] 开始导出CSV...')

    const {
      filename = `表格数据_${new Date().toISOString().split('T')[0]}`,
      formatData
    } = options

    // 过滤可导出的列
    const exportColumns = columns.filter(isExportableColumn)
    console.log('📋 [EXPORT] 过滤后的导出列:', exportColumns.map(col => col.dataIndex))

    // 构建CSV内容
    const headers = exportColumns.map(col => col.title || col.dataIndex)
    const csvRows = [headers.join(',')]

    data.forEach(row => {
      const csvRow = exportColumns.map(col => {
        let value = row[col.dataIndex]

        // 使用自定义格式化函数（如果提供）
        if (formatData) {
          value = formatData(value, col.dataIndex, row)
        }

        // 处理包含逗号、换行符或引号的字段
        if (typeof value === 'string') {
          if (value.includes(',') || value.includes('\n') || value.includes('"')) {
            // 转义引号并用引号包围
            return `"${value.replace(/"/g, '""')}"`
          }
        }
        return value || ''
      })
      csvRows.push(csvRow.join(','))
    })

    const csvContent = csvRows.join('\n')

    // 添加BOM以确保Excel正确显示中文
    const blob = new Blob([`\ufeff${csvContent}`], {
      type: 'text/csv;charset=utf-8;'
    })

    let filenameNoExt = filename || ''
    if (filenameNoExt.endsWith('.csv')) {
      filenameNoExt = filenameNoExt.substring(0, filenameNoExt.length - 4)
    }
    saveAs(blob, `${filenameNoExt}.csv`)

    console.log('✅ [EXPORT] CSV导出成功:', `${filenameNoExt}.csv`)
    return true

  } catch (error: any) {
    console.error('❌ [EXPORT] CSV导出失败:', error)
    throw new Error('CSV导出失败')
  }
}

/**
 * 默认格式化函数
 * @param item 数据项
 * @param lang 语言
 * @returns 格式化后的数据
 */
export const itemFormatterDefault = (item: any, lang: string) => {
  if (lang === 'zh-CN') {
    return item?.nameCn || item?.enterpriseName || item?.name || item?.labelZh || ''
  } else {
    return item?.nameEn || item?.enterpriseNameEn || item?.labelEn || item?.nameEn || ''
  }
}

/**
 * 港口格式化函数
 * @param item 数据项
 * @param lang 语言
 * @returns 格式化后的数据
 */
export const itemFormatterSeaport = (item: any, lang: string) => {
  return lang === 'zh-CN' ? `${item?.code} | ${item?.nameCn}` : `${item?.code} | ${item?.nameEn}`
}

/**
 * 格式化基础数据
 * @param value 数据值
 * @param type 类型
 * @param basicDataMap 基础数据Map，来自BatchBaseDataProvider
 * @param formatFn 格式化函数
 * @returns 格式化后的数据
 */
export const formatByBasicDataMap = (value: any, type: string, basicDataMap: any, formatFn: (item: any, lang: string) => string = itemFormatterDefault) => {
  if (!value || !basicDataMap) {
    return ''
  }

  // 获取对应类型的基础数据数组
  const typeList = basicDataMap?.[type]
  if (!typeList) {
    return ''
  }

  // 根据ID进行匹配，并输出格式化结果
  const item = typeList.find(item => item?.id === value)
  const lang = getCurrentLocale()
  return item ? formatFn(item, lang) : ''
}
